import type { NoticeShapeKind, NoticeShapeLayer } from './types.js';

// =============================================================================
// Shape layers — one SVG path string drives the editor (<path d>), the thumbnail
// and the Player (Path2D), so all three draw the same outline.
// =============================================================================

export const NOTICE_SHAPE_LABELS: Record<NoticeShapeKind, string> = {
  rectangle: '네모', ellipse: '원', line: '라인', triangle: '세모', speechBubble: '말풍선', star: '별', polygon: '다각형',
};

type ShapeGeometry = Pick<NoticeShapeLayer, 'shape' | 'width' | 'height' | 'stroke' | 'strokeWidth' | 'cornerRadius' | 'sides' | 'lineDirection'>;

/** Sides/points are clamped here, the one place every renderer passes through, so a bad value in a saved
 *  slide cannot make a device loop over millions of vertices. */
export function noticeShapeSides(layer: Pick<NoticeShapeLayer, 'shape' | 'sides'>): number {
  const value = layer.sides;
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(Math.max(3, Math.min(12, value))) : layer.shape === 'star' ? 5 : 6;
}

/** Path in the layer's own box (0,0)–(width,height). The outline is inset by half the stroke width so the
 *  stroke stays inside the box the editor selects and resizes. */
export function noticeShapePath(layer: ShapeGeometry): string {
  const { width: W, height: H } = layer;
  const half = layer.stroke ? Math.max(0, layer.strokeWidth || 0) / 2 : 0;
  const x0 = Math.min(half, W / 2), y0 = Math.min(half, H / 2), x1 = W - x0, y1 = H - y0;
  const w = x1 - x0, h = y1 - y0, cx = W / 2, cy = H / 2;
  const n = (value: number) => String(Math.round(value * 100) / 100);
  const poly = (points: [number, number][]) => `M${points.map(([x, y]) => `${n(x)} ${n(y)}`).join('L')}Z`;
  const radius = (max: number) => Math.max(0, Math.min(max, Number.isFinite(layer.cornerRadius) ? layer.cornerRadius! : 0));
  const corner = (r: number, x: number, y: number) => r > 0 ? `A${n(r)} ${n(r)} 0 0 1 ${n(x)} ${n(y)}` : `L${n(x)} ${n(y)}`;
  switch (layer.shape) {
    case 'line': {
      const [ax, ay, bx, by] = layer.lineDirection === 'vertical' ? [cx, y0, cx, y1] : layer.lineDirection === 'down' ? [x0, y0, x1, y1]
        : layer.lineDirection === 'up' ? [x0, y1, x1, y0] : [x0, cy, x1, cy];
      return `M${n(ax)} ${n(ay)}L${n(bx)} ${n(by)}`;
    }
    case 'ellipse':
      return `M${n(x0)} ${n(cy)}A${n(w / 2)} ${n(h / 2)} 0 1 0 ${n(x1)} ${n(cy)}A${n(w / 2)} ${n(h / 2)} 0 1 0 ${n(x0)} ${n(cy)}Z`;
    case 'triangle':
      return poly([[cx, y0], [x1, y1], [x0, y1]]);
    case 'speechBubble': {
      // Body is the top 78 %; the tail hangs from the lower-left edge of the body.
      const bottom = y0 + h * 0.78, r = radius(Math.min(w * 0.2, (bottom - y0) / 2));
      return `M${n(x0 + r)} ${n(y0)}H${n(x1 - r)}${corner(r, x1, y0 + r)}V${n(bottom - r)}${corner(r, x1 - r, bottom)}`
        + `H${n(x0 + w * 0.4)}L${n(x0 + w * 0.12)} ${n(y1)}L${n(x0 + w * 0.2)} ${n(bottom)}H${n(x0 + r)}${corner(r, x0, bottom - r)}V${n(y0 + r)}${corner(r, x0 + r, y0)}Z`;
    }
    case 'star':
    case 'polygon': {
      const sides = noticeShapeSides(layer), star = layer.shape === 'star', count = star ? sides * 2 : sides;
      return poly(Array.from({ length: count }, (_, i) => {
        const angle = -Math.PI / 2 + i * 2 * Math.PI / count, scale = star && i % 2 ? 0.4 : 1;
        return [cx + Math.cos(angle) * w / 2 * scale, cy + Math.sin(angle) * h / 2 * scale] as [number, number];
      }));
    }
    default: {
      const r = radius(Math.min(w, h) / 2);
      return `M${n(x0 + r)} ${n(y0)}H${n(x1 - r)}${corner(r, x1, y0 + r)}V${n(y1 - r)}${corner(r, x1 - r, y1)}H${n(x0 + r)}${corner(r, x0, y1 - r)}V${n(y0 + r)}${corner(r, x0 + r, y0)}Z`;
    }
  }
}

/** Canvas renderer for the Player and the thumbnail. Draws at layer.x/y in the current transform. */
export function drawNoticeShape(ctx: CanvasRenderingContext2D, layer: NoticeShapeLayer): void {
  if (typeof Path2D === 'undefined') return;
  const path = new Path2D(noticeShapePath(layer));
  ctx.save();
  ctx.translate(layer.x, layer.y);
  if (layer.fill && layer.shape !== 'line') { ctx.fillStyle = layer.fill; ctx.fill(path); }
  if (layer.stroke && layer.strokeWidth > 0) {
    ctx.strokeStyle = layer.stroke; ctx.lineWidth = layer.strokeWidth; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.stroke(path);
  }
  ctx.restore();
}
